import React, {
    useEffect,
    useState,
    useContext,
    useReducer,
} from 'react';
import { useLocation, Outlet } from 'react-router-dom';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';

import { makeStyles, ThemeProvider as MuiThemeProvider } from '@mui/styles';
import { ThemeProvider } from '@mui/material/styles';
import { StyledEngineProvider } from '@mui/styled-engine';
import CssBaseline from '@mui/material/CssBaseline';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
// import MomentUtils from '@date-io/moment';

import Themes from '../../data/themes';

import SideNav from '../../components/sidenav/ls';
import TopNav from '../../components/topnav';
import Notifications from '../../components/notifications';
import { Copyright } from '../../components/messages/footer';

import Auth, { ProfileContext } from '../../data/auth-service';
import { UpdateReducer, TranslateTheme } from '../../libraries/helpers';
import { blue, neutrals1 } from '../../data/colors';
import DataContext from '../../data/data-context';

const useStyles = makeStyles(theme => ({
    content: {
        flexGrow: 1,
        paddingLeft: theme.spacing(5),
        paddingRight: theme.spacing(3),
        paddingBottom: theme.spacing(3),
        transition: 'margin-left 500ms',
    },

    // this only needs to be defined once in the app
    '@global': {
        '.react-codemirror2': {
            '& .cm-s-material': {
                height: 'auto',
                border: `solid 1px ${theme.palette.primary.dark}`,
                backgroundColor: blue[900],
                padding: 5,
                fontSize: 15,
            },
            '& .cm-s-xq-light': {
                height: 'auto',
                border: `solid 1px ${theme.palette.primary.dark}`,
                backgroundColor: neutrals1[0],
                padding: 5,
                fontSize: 15,
            },

            '& .CodeMirror.CodeMirror-focused': {
                border: `solid 1px ${theme.palette.primary.main}`,
            },
        },
    },
}));

const AppWrapper = () => {
    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={Themes.DarkTheme}>
                <MuiThemeProvider theme={Themes.DarkTheme}>
                    <App />
                </MuiThemeProvider>
            </ThemeProvider>
        </StyledEngineProvider>
    );
};

const App = (props) => {
    const classes = useStyles();
    const location = useLocation();

    const [offset, setOffset] = useState(135);
    const [messages, setMessages] = useState({
        header: '',
        footer: '',
    });

    const [theme, setTheme] = useState(Themes.DarkTheme);

    const { updateProfile, getProfile } = useContext(ProfileContext);

    const setSidenav = (s, m) => {
        _setSidenav({ ...s, ...m });
    };

    // const [sidenav, _setSidenav] = useState({});
    const [sidenav, _setSidenav] = useReducer(UpdateReducer, { collapsed: false, panels: {} });

    const changeTheme = themename => {
        const t = themename === 'light' ? Themes.LightTheme : Themes.DarkTheme;
        setTheme(current => {
            return { ...t, date: { ...current.date, ...getProfile('date') } };
        });

        // update cookie
        Auth.updateTheme(themename);
    };

    const changeDateFormat = formats => {
        // const date = { ...theme.date, ...formats };
        updateProfile('date', formats);
        setTheme(current => {
            return { ...current, date: { ...current.date, ...formats } };
        });

        // update cookie
        Auth.updateDates(formats);
    };

    useEffect(() => {
        let stopNotification;

        const themename = TranslateTheme(getProfile('colorscheme')) || 'dark';
        const date = getProfile('date');
        const datheme = themename === 'light' ? Themes.LightTheme : Themes.DarkTheme;
        setTheme({ ...datheme, date: { ...datheme.date, ...date } });

        // no need to stash this update to profile
        _setSidenav(getProfile('nav'));

        // Konami Code, shhhhh....
        (() => {
            const s = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a', 'Enter'];
            let cs = [];
            const tf = () => { cs = [] };
            let t = setTimeout(tf, 1500);

            window.addEventListener('keydown', e => {
                clearTimeout(t);
                cs.push(e.key);

                const eq = s.length === cs.length && s.every((v, i) => v === cs[i]);
                if (eq) {
                    toast.dark('You found a secret!');
                }
                t = setTimeout(tf, 1500);
            });
        })();

        return () => {
            console.log('Thank You Come Again');
            stopNotification && clearInterval(stopNotification);
        };
    }, [getProfile]);

    // sidenav.sidebar has three states - undefined, 'open' or 'collapsed'
    useEffect(() => {
        let o = 60;
        if (!sidenav.collapsed) o = 300;
        if (sidenav.sidebar === 'open') o += 300;
        else if (sidenav.sidebar === 'collapsed') o += 60;
        setOffset(o);
    }, [sidenav]);

    return (
        <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
                <MuiThemeProvider theme={theme}>
                    <LSThemeChanger.Provider value={{ changeTheme, changeDateFormat }}>
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DataContext.Provider value={{
                                sidenav,
                                setSidenav,
                            }}>
                                <React.Fragment>
                                    <CssBaseline />
                                    <Notifications />
                                    <Copyright />
                                    <TopNav header={Boolean(messages.header)} />
                                    <SideNav header={Boolean(messages.header)} path={location.pathname}/>
                                    <main className={classes.content} style={{
                                        marginLeft: (!location.pathname.match('eula') && offset),
                                    }}>
                                        <Outlet />
                                    </main>
                                </React.Fragment>
                            </DataContext.Provider>
                        </LocalizationProvider>
                    </LSThemeChanger.Provider>
                </MuiThemeProvider>
            </ThemeProvider>
        </StyledEngineProvider>
    );
};

App.propTypes = {
    children: PropTypes.node,
    location: PropTypes.object,
};

const LSThemeChanger = React.createContext({
    changeTheme: theme => {},
    changeDateFormat: formats => {},
});

export {
    App as default,
    AppWrapper,
    LSThemeChanger
};
